import React, { useState, useContext } from "react";
import { register } from "../services/authService";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import "./Register.css";

const Register = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isOver18, setIsOver18] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!isOver18) {
      setError("您必須年滿 18 歲才能註冊。");
      return;
    }
    if (!agreedToTerms) {
      setError("您必須同意用戶規則才能註冊。");
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        username,
        email,
        password,
        is_over_18: isOver18,
        agreed_to_terms: agreedToTerms,
      };
      await register(payload);
      setSuccess("註冊成功！正在為您登入...");

      // 延遲登入，讓用戶看到成功訊息
      setTimeout(async () => {
        try {
          await login(username, password);
          navigate("/dashboard");
        } catch (loginError) {
          setError("註冊成功但自動登入失敗，請手動登入。");
          setTimeout(() => navigate("/login"), 2000);
        }
      }, 1500);
    } catch (error) {
      console.error("註冊失敗:", error);
      if (error.response?.data) {
        const errorData = error.response.data;
        let errorMessage = "註冊失敗：";

        if (errorData.username && Array.isArray(errorData.username) && errorData.username.length > 0) {
          errorMessage += `用戶名${errorData.username[0]} `;
        }
        if (errorData.email && Array.isArray(errorData.email) && errorData.email.length > 0) {
          errorMessage += `電子郵件${errorData.email[0]} `;
        }
        if (errorData.password && Array.isArray(errorData.password) && errorData.password.length > 0) {
          errorMessage += `密碼${errorData.password[0]} `;
        }
        if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors) && errorData.non_field_errors.length > 0) {
          errorMessage += errorData.non_field_errors[0];
        }

        setError(errorMessage);
      } else {
        setError("註冊失敗，請稍後再試。");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-header">
        <h2>加入我們</h2>
        <p>創建您的帳號，開始精彩的學習之旅</p>
      </div>

      <form className="register-form" onSubmit={handleSubmit}>
        <div className="input-group">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="用戶名"
            required
            disabled={isLoading}
          />
        </div>

        <div className="input-group">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="電子郵件"
            required
            disabled={isLoading}
          />
        </div>

        <div className="input-group">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="密碼"
            required
            disabled={isLoading}
          />
        </div>

        <div className="checkbox-section">
          <div className="checkbox-row">
            <input
              type="checkbox"
              id="age-checkbox"
              checked={isOver18}
              onChange={(e) => setIsOver18(e.target.checked)}
              required
              disabled={isLoading}
            />
            <label htmlFor="age-checkbox">
              我已年滿 18 歲
            </label>
          </div>

          <div className="checkbox-row">
            <input
              type="checkbox"
              id="terms-checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              required
              disabled={isLoading}
            />
            <label htmlFor="terms-checkbox">
              我已閱讀並同意{" "}
              <button
                type="button"
                className="terms-link"
                onClick={() => setShowTermsModal(true)}
                disabled={isLoading}
              >
                用戶規則
              </button>
            </label>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <button
          type="submit"
          className="submit-btn"
          disabled={isLoading}
        >
          {isLoading ? "註冊中..." : "🎯 立即註冊"}
        </button>
      </form>

      <div className="register-footer">
        <p>
          已經有帳號了？{" "}
          <Link to="/login" className="login-link">
            立即登入
          </Link>
        </p>
      </div>

      {showTermsModal && (
        <div className="terms-modal" onClick={() => setShowTermsModal(false)}>
      <div
  className="terms-content"
  onClick={(e) => e.stopPropagation()}
>
  <h3>📋 用戶規則與條款</h3>
  <p>為維護平台秩序與用戶權益，請所有會員務必詳讀並遵守以下規範：</p>

  <h4>一、內容發布與閱覽規範（核心條款）</h4>
  <h5>📚 著作權保護</h5>
  <ul>
    <li>本站接受一文多投。</li>
    <li>
      曾發表於其他平台之文章，請務必註明原始出處，
      以避免被視為盜文行為。
    </li>
  </ul>

  <h5>🚫 八大禁令</h5>
  <ul>
    <li>嚴禁政治、宗教相關爭議或攻擊性言論。</li>
    <li>嚴禁透露任何個人資訊（如電話、姓名、LINE、微信等）。</li>
    <li>嚴禁帶入私人恩怨或感情糾紛。</li>
    <li>嚴禁人身攻擊、挑釁或謾罵行為。</li>
    <li>不歡迎火星文、洗版或與主題無關之回應。</li>
    <li>不喜歡內容請直接離開，禁止無理取鬧。</li>
    <li>嚴禁商業欺詐、金錢借貸、吸毒等違法行為。</li>
    <li>嚴禁「資深會員壓榨新手」的惡意盈利行為。</li>
  </ul>

  <h4>二、會員升級與積分制度</h4>
  <p>
    會員升級制度目前仍在規劃中，將視以下方案進行討論與調整：
  </p>
  <ul>
    <li>純討論升級制度</li>
    <li>混合升級制度（行為＋積分）</li>
  </ul>
  <p>
    本制度將同時考量相關法律風險，
    尤其涉及猥褻內容散播之規範。
  </p>
  <p>
    積分獲取機制將另行公告。
  </p>

  <h4>三、揪團與商業行為限制</h4>
  <ul>
    <li>
      <strong>揪團限制：</strong>
      嚴禁於一般版面（如寫真館、悅覽館）發表糾團出遊文章。
    </li>
    <li>
      <strong>商業判定：</strong>
      置入性行銷由版主團投票認定，違規者將直接刪文。
    </li>
    <li>
      <strong>安全宣告：</strong>
      私人聯誼所產生之消費、拆帳或糾紛，
      站方概不負責，且禁止於公共區域發文爭吵。
    </li>
  </ul>

  <button onClick={() => setShowTermsModal(false)}>
    ✅ 我已閱讀並同意以上條款
  </button>
</div>
</div>
      )}
    </div>
  );
};

export default Register;
